import { useAuth } from '@/contexts/AuthContext'
import { usePostHog } from '@/contexts/PostHogContext'

// Enhanced analytics hook that automatically includes user context in all events
export const useAnalyticsWithUser = () => {
  const { track, identify, reset, isInitialized } = usePostHog()
  const { user, organization, isAuthenticated } = useAuth()

  // Build user context for automatic inclusion in events
  const getUserContext = () => {
    if (!isAuthenticated || !user) {
      return {}
    }

    return {
      user_id: user.id,
      user_email: user.email,
      user_name: user.name,
      user_role: user.role,
      organization_id: organization?.id,
      organization_name: organization?.name,
      organization_plan_type: organization?.plan?.type,
    }
  }

  const trackEvent = (event: string, properties?: Record<string, any>) => {
    const userContext = getUserContext()
    track(event, {
      ...userContext,
      ...properties,
    })
  }

  const trackUserAction = (action: string, properties?: Record<string, any>) => {
    const userContext = getUserContext()
    track(`user_${action}`, {
      ...userContext,
      ...properties,
    })
  }

  const trackFileAction = (action: string, fileType?: string, properties?: Record<string, any>) => {
    const userContext = getUserContext()
    track(`file_${action}`, {
      file_type: fileType,
      ...userContext,
      ...properties,
    })
  }

  const trackFolderAction = (action: string, properties?: Record<string, any>) => {
    const userContext = getUserContext()
    track(`folder_${action}`, {
      ...userContext,
      ...properties,
    })
  }

  const trackPageView = (pageName: string, properties?: Record<string, any>) => {
    const userContext = getUserContext()
    track('page_view', {
      page: pageName,
      ...userContext,
      ...properties,
    })
  }

  return {
    trackEvent,
    trackUserAction,
    trackFileAction,
    trackFolderAction,
    trackPageView,
    identify,
    reset,
    isInitialized,
    // Also return user context for manual use if needed
    getUserContext,
  }
}

// Export as default analytics hook with user context
export const useAnalytics = useAnalyticsWithUser
